/**
 * FIFO of messages that must survive a lost connection (race events). Every item carries a
 * tag (the race id) so the queue can be flushed selectively after a reconnect: events of a
 * race that ended while the player was offline must not leak into the next race.
 */

export interface OutboxEntry<T> {
  item: T;
  tag: string;
  /**
   * The item was already sent, but the connection broke before the server confirmed it:
   * it may or may not have arrived.
   */
  maybeDelivered: boolean;
}

export class Outbox<T> {
  private entries: Array<OutboxEntry<T>> = [];

  /** @param limit oldest items are dropped beyond this (protects memory on long outages) */
  constructor(private readonly limit = 500) {}

  get size(): number {
    return this.entries.length;
  }

  push(item: T, tag: string): void {
    this.entries.push({ item, tag, maybeDelivered: false });
    this.trim();
  }

  /**
   * Puts items that were sent on a connection that broke back in front of the queue: they
   * are older than anything queued since.
   */
  requeue(sent: ReadonlyArray<{ item: T; tag: string }>): void {
    const again = sent.map(({ item, tag }) => ({ item, tag, maybeDelivered: true }));
    this.entries = [...again, ...this.entries];
    this.trim();
  }

  /** Empties the queue and returns, in order, the entries that pass `keep`. */
  drain(keep: (entry: OutboxEntry<T>) => boolean): Array<OutboxEntry<T>> {
    const kept = this.entries.filter(keep);
    this.entries = [];
    return kept;
  }

  clear(): void {
    this.entries = [];
  }

  private trim(): void {
    if (this.entries.length > this.limit) {
      this.entries.splice(0, this.entries.length - this.limit);
    }
  }
}
